import React, { memo, useCallback, useContext, useEffect, useMemo } from 'react';
import type { Unit } from 'sr-types/lib/search/v1/graphql';
import { DEFAULT_DURATION_UNIT } from '../../common/components/schedule/constants';
import ScheduleFormWidget from '../../common/components/schedule/ScheduleFormWidget';
import { ScheduleFormWidgetValue, SupportedSchedulerDurationUnit } from '../../common/components/schedule/types';
import { FORM_WIDGET_COMPONENTS } from '../../common/form/formConstants';
import { FormContext } from '../../common/form/FormContext';
import FormItem from '../../common/form/FormItem';
import FormWidget from '../../common/form/FormWidget';
import { useI18n } from '../../common/i18n/I18n';
import type { ActivityFormType } from '../helpers/activityUtils';
import { ACTIVITY_TYPES } from './activityConstants';

const ACTIVITY_SCHEDULE_ALLOWED_DURATION_UNITS: SupportedSchedulerDurationUnit[] = ['hour', 'day', 'week'];

type ActivityScheduleFormWidgetProps = {
    isScheduleDisabled?: boolean;
    durationUnits?: Unit[];
};

const hasMilestoneScheduleDefaults = (dateRange: ActivityFormType['dateRange']) => {
    return (
        dateRange?.durationSpecified === false &&
        dateRange?.dateTimeDuration?.unit === DEFAULT_DURATION_UNIT &&
        dateRange?.dateTimeDuration?.value === 1 &&
        !dateRange?.end &&
        !dateRange?.endTime
    );
};

const toScheduleFormWidgetValue = (dateRange: ActivityFormType['dateRange']): ScheduleFormWidgetValue => {
    return {
        durationSpecified: Boolean(dateRange?.durationSpecified),
        startDate: dateRange?.start || '',
        startTime: dateRange?.startTime || '',
        endDate: dateRange?.end || '',
        endTime: dateRange?.endTime || '',
        duration: {
            unit: (dateRange?.dateTimeDuration?.unit as SupportedSchedulerDurationUnit) || DEFAULT_DURATION_UNIT,
            value: dateRange?.dateTimeDuration?.value
        }
    };
};

function ActivityScheduleFormWidgetNonMemo({ isScheduleDisabled = false, durationUnits }: ActivityScheduleFormWidgetProps) {
    const { setState, state } = useContext(FormContext);
    const startDateLabel = useI18n('scheduleFormWidget.startDate.label');
    const isMilestone = state?.activityType === ACTIVITY_TYPES.MILESTONE;
    const dateRange = state?.dateRange;
    const hasNormalizedMilestoneSchedule = hasMilestoneScheduleDefaults(dateRange);

    useEffect(() => {
        if (!isMilestone || hasNormalizedMilestoneSchedule) {
            return;
        }

        setState((prevState: ActivityFormType) => ({
            ...prevState,
            durationSpecified: false,
            dateRange: {
                ...prevState.dateRange,
                durationSpecified: false,
                end: '',
                endTime: '',
                dateTimeDuration: {
                    ...prevState.dateRange?.dateTimeDuration,
                    unit: DEFAULT_DURATION_UNIT,
                    value: 1
                }
            }
        }));
    }, [hasNormalizedMilestoneSchedule, isMilestone, setState]);

    const scheduleValue = useMemo(() => toScheduleFormWidgetValue(dateRange), [dateRange]);

    const handleScheduleChange = useCallback(
        (nextValue: ScheduleFormWidgetValue) => {
            setState((prevState: ActivityFormType) => ({
                ...prevState,
                durationSpecified: nextValue.durationSpecified,
                dateRange: {
                    ...prevState.dateRange,
                    start: nextValue.startDate,
                    startTime: nextValue.startTime || '',
                    end: nextValue.endDate || '',
                    endTime: nextValue.endTime || '',
                    durationSpecified: nextValue.durationSpecified,
                    dateTimeDuration: {
                        ...prevState.dateRange?.dateTimeDuration,
                        unit: nextValue.duration.unit,
                        value: nextValue.duration.value
                    }
                }
            }));
        },
        [setState]
    );

    if (isMilestone) {
        return (
            <FormItem>
                <FormWidget
                    component={FORM_WIDGET_COMPONENTS.date}
                    name="dateRange.start"
                    label={startDateLabel}
                    disabled={isScheduleDisabled}
                    deferKeyboardDateCommitUntilBlur
                />
            </FormItem>
        );
    }

    return (
        <ScheduleFormWidget
            id="activity-schedule"
            variant="standard-date-only"
            value={scheduleValue}
            onChange={handleScheduleChange}
            allowedDurationUnits={ACTIVITY_SCHEDULE_ALLOWED_DURATION_UNITS}
            durationUnits={durationUnits}
            specifyDurationFormFieldLabelToken="production.activityForm.specifyDuration"
            disabled={isScheduleDisabled}
        />
    );
}

const ActivityScheduleFormWidget = memo(ActivityScheduleFormWidgetNonMemo);
export default ActivityScheduleFormWidget;
